import { z } from 'zod';

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}

const LogEventSchema = z.object({
  level: z.nativeEnum(LogLevel),
  message: z.string(),
  timestamp: z.date(),
  context: z.record(z.unknown()).optional(),
  error: z.unknown().optional()
});

type LogEvent = z.infer<typeof LogEventSchema>;

class Logger {
  private static instance: Logger;
  private readonly MAX_LOG_SIZE = 1000;
  private logs: LogEvent[] = [];
  private readonly BATCH_SIZE = 50;
  private batchTimeout: NodeJS.Timeout | null = null;
  
  private constructor() {
    this.setupErrorBoundary();
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private setupErrorBoundary() {
    if (typeof window !== 'undefined') {
      window.addEventListener('error', (event) => {
        this.error('Uncaught error:', event.error);
      });

      window.addEventListener('unhandledrejection', (event) => {
        this.error('Unhandled promise rejection:', event.reason);
      });
    }
  }

  private async log(event: LogEvent) {
    try {
      const validatedEvent = LogEventSchema.parse({
        ...event,
        timestamp: event.timestamp || new Date()
      });

      this.logs.push(validatedEvent);

      // Keep logs under size limit
      if (this.logs.length > this.MAX_LOG_SIZE) {
        this.logs = this.logs.slice(-this.MAX_LOG_SIZE);
      }

      // Batch processing for production
      if (!import.meta.env.DEV) {
        this.scheduleBatchProcessing();
      } else {
        console.log(
          `[${validatedEvent.timestamp.toISOString()}] ${validatedEvent.level.toUpperCase()}: ${validatedEvent.message}`,
          validatedEvent.context || ''
        );
      }
    } catch (error) {
      console.error('Logger validation error:', error);
    }
  }

  private scheduleBatchProcessing() {
    if (this.batchTimeout) return;

    this.batchTimeout = setTimeout(() => {
      this.processBatch();
      this.batchTimeout = null;
    }, 1000);
  }

  private processBatch() {
    const batch = this.logs.slice(-this.BATCH_SIZE);
    // In a real app, send to logging service
    console.log(`Processing ${batch.length} logs`);
  }

  debug(message: string, context?: Record<string, unknown>) {
    this.log({ level: LogLevel.DEBUG, message, timestamp: new Date(), context });
  }

  info(message: string, context?: Record<string, unknown>) {
    this.log({ level: LogLevel.INFO, message, timestamp: new Date(), context });
  }

  warn(message: string, context?: Record<string, unknown>) {
    this.log({ level: LogLevel.WARN, message, timestamp: new Date(), context });
  }

  error(message: string, error?: unknown, context?: Record<string, unknown>) {
    this.log({
      level: LogLevel.ERROR,
      message,
      timestamp: new Date(),
      context,
      error
    });
  }

  getLogs(level?: LogLevel, limit?: number): LogEvent[] {
    let filteredLogs = this.logs;
    if (level) {
      filteredLogs = filteredLogs.filter(log => log.level === level);
    }
    if (limit) {
      filteredLogs = filteredLogs.slice(-limit);
    }
    return filteredLogs;
  }

  clearLogs() {
    this.logs = [];
  }
}

export const logger = Logger.getInstance();
